import { DestroyRef, Injectable, NgZone, inject, signal } from '@angular/core';
import { AuthTokenService } from './auth-token.service';
import { SessionRedirectService } from './session-redirect.service';

/**
 * One app-wide "your session has expired" flag (VE-SIGNALR), shown by the single
 * `app-session-expired-banner` in the app shell. The live connection sets it when its token expires or the hub
 * answers 401; it clears itself as soon as a fresh, live token appears (a sign-in in another tab). Root-provided,
 * so however many components use the live connection there is only one flag and one banner.
 */
@Injectable({ providedIn: 'root' })
export class SessionExpiryNoticeService {
  private readonly auth = inject(AuthTokenService);
  private readonly sessionRedirect = inject(SessionRedirectService);
  private readonly ngZone = inject(NgZone);

  private readonly expiredState = signal(false);
  /** True while the banner should be shown. */
  readonly expired = this.expiredState.asReadonly();

  constructor() {
    const sub = this.auth.tokenChanges?.subscribe(() => {
      if (this.auth.hasLiveToken()) this.ngZone.run(() => this.expiredState.set(false));
    });
    if (sub) inject(DestroyRef).onDestroy(() => sub.unsubscribe());
  }

  /** The session is over for the live connection: show the banner (idempotent). */
  markExpired(): void {
    if (this.expiredState()) return;
    this.ngZone.run(() => this.expiredState.set(true));
  }

  /** A fresh token is in use again: hide the banner. */
  clear(): void {
    if (!this.expiredState()) return;
    this.ngZone.run(() => this.expiredState.set(false));
  }

  /** The banner's Sign in button: legacy login, back to this page where a handoff exists. */
  signIn(): void {
    this.sessionRedirect.signInAgain();
  }
}
